import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useDispatch, useSelector } from "react-redux";
import { Batch, RootState } from "@/types";
import { useGetAllQuery, useUpadteStudentMutation } from "../store/studentApi";
import { useNavigate, useParams } from "react-router-dom";
import useFormData from "@/hooks/useFormData";
import { userRegister } from "../store/user/userSlice";
import Spinner from "./Spinner";
import { toast } from "sonner";
import { useGetBatchQuery } from "../store/batchApi";
import { Select, SelectContent, SelectItem, SelectValue } from "./ui/select";
import { SelectTrigger } from "@radix-ui/react-select";
import { useGetAllCourseQuery } from "@/store/courseApi";

const AddStudents = () => {
  const { user, isLoading } = useSelector((state: RootState) => state.user);
  const { data: students } = useGetAllQuery(user?.token);
  const { data: batches } = useGetBatchQuery();
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [UpadteStudent, { isLoading: isUpdating }] = useUpadteStudentMutation();

  const { data: courses } = useGetAllCourseQuery();

  const filterStudents = id ? students?.find((std) => std._id === id) : null;

  const defaultFormData = {
    fname: "",
    lname: "",
    father: "",
    curAddress: "",
    perAddress: "",
    dob: "",
    gender: "",
    status: "",
    isCompleted: "No", // Set default to "No"
    course: "",
    mode: "",
    cduration: "",
    isPlaced: "No", // Set default to "No"
    role: "",
    degree: "",
    comYear: "",
    university: "",
    contact: "",
    altContact: "",
    linkedIn: "",
    joiningDate: "",
    endDate: "",
    email: "",
    password: "",
    profile: "",
    batch: "",
    rank: "",
    fee: "",
    Paidfee: "",
    payMode: "",
    username: "",
    curtech: "",
  };

  const { formData, handleChange } = useFormData(
    filterStudents || defaultFormData
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!id) {
        const res = await dispatch(userRegister(formData));

        if (res?.payload) {
          toast.success("User Registered Successfully");
          navigate("/students");
        } else {
          toast.error(res?.payload);
          // toast.error(res?.error?.data?.message || res?.error || res?.data.error || "Failed to Create User!");
        }
      } else {
        const res = await UpadteStudent({
          id,
          Token: user?.token,
          userData: formData,
        });
        if (res?.data) {
          toast.success("User Updated Successfully");
          navigate("/students"); // Ensure redirection after update if needed
        } else {
          toast.error(res?.error?.data?.message || "Failed to update user");
        }
      }
    } catch (error) {
      toast.error(error || "Something went wrong");
    }
  };

  const formattedDate = (date: string) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  if (isLoading) {
    <span className="loading loading-infinity loading-lg"></span>;
  }

  return (
    <Card className="w-full max-w-4xl mx-auto my-6">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          <h1> Student Registration Form</h1>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "fname", label: "First Name", required: true },
              { id: "lname", label: "Last Name", required: true },
              {
                id: "father",
                label: "Father's Name / Husband's Name",
                required: true,
              },
              { id: "username", label: "Username", required: true },
              { id: "profile", label: "Profile Image URL", required: false },
              { id: "password", label: "Password", required: true },
              // ...(!id
              //   ? [{ id: "password", label: "Password", required: true }]
              //   : []),
              {
                id: "dob",
                label: "Date of Birth",
                type: "date",
                required: true,
              },
              {
                id: "joiningDate",
                label: "Joining Date",
                type: "date",
                required: true,
              },
            ].map(({ id, label, type = "text", required }) => (
              <div className="space-y-2" key={id}>
                <Label htmlFor={id}>
                  {label} {required && "*"}
                </Label>
                <Input
                  id={id}
                  type={type}
                  value={
                    type === "date"
                      ? formattedDate(formData[id])
                      : formData[id] || ""
                  }
                  onChange={handleChange}
                  required={required}
                  className="w-full"
                />
              </div>
            ))}
          </div>

          {/* Address Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "curAddress", label: "Current Address", required: true },
              { id: "perAddress", label: "Permanent Address", required: true },
            ].map(({ id, label, required }) => (
              <div className="space-y-2" key={id}>
                <Label htmlFor={id}>
                  {label} {required && "*"}
                </Label>
                <Textarea
                  id={id}
                  value={formData[id] || ""}
                  onChange={handleChange}
                  required={required}
                  className="w-full min-h-[100px]"
                />
              </div>
            ))}
          </div>

          {/* Gender Selection */}
          <div className="space-y-2">
            <Label>Gender *</Label>
            <RadioGroup
              value={formData.gender}
              onValueChange={(value) => handleChange({ id: "gender", value })}
              className="flex space-x-4"
            >
              {["male", "female"].map((value) => (
                <div className="flex items-center space-x-2" key={value}>
                  <RadioGroupItem value={value} id={value} required />
                  <Label htmlFor={value}>
                    {value.charAt(0).toUpperCase() + value.slice(1)}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "contact", label: "Contact Number", required: true },
              { id: "altContact", label: "Alternate Contact" },
              {
                id: "email",
                label: "Email Address",
                type: "email",
                required: true,
              },
              { id: "linkedin", label: "LinkedIn Profile URL" },
            ].map(({ id, label, type = "text", required }) => (
              <div className="space-y-2" key={id}>
                <Label htmlFor={id}>
                  {label} {required && "*"}
                </Label>
                <Input
                  id={id}
                  type={
                    id === "contact" || id === "altContact" ? "number" : type
                  }
                  value={formData[id] || ""}
                  onChange={handleChange}
                  required={required}
                  className={`w-full ${id === "contact" || id === "altContact" ? "no-arrows" : ""
                    }`}
                />
              </div>
            ))}
          </div>

          {/* Education and Course Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { id: "degree", label: "Degree", required: true },
              { id: "university", label: "University", required: true },
              {
                id: "comYear",
                label: "Year of Completion",
                type: "number",
                required: true,
              },
              { id: "cduration", label: "Duration of Course", required: true },
              ...(id
                ? []
                : [
                  { id: "fee", label: "Fee", required: true },
                  { id: "Paidfee", label: "Paid", required: true },
                ]),
            ].map(({ id, label, type = "text", required }) => (
              <div className="space-y-2" key={id}>
                <Label htmlFor={id}>
                  {label} {required && "*"}
                </Label>
                <Input
                  id={id}
                  type={
                    id === "degree" || id === "university" || id === "cduration"
                      ? type
                      : "number"
                  }
                  value={formData[id] || ""}
                  onChange={handleChange}
                  required={required}
                  className={`w-full ${id !== "degree" && id !== "university" && id !== "cduration"
                    ? "no-arrows"
                    : ""
                    }`}
                />
              </div>
            ))}

            {/* Payment Mode */}
            <div className="space-y-2">
              <Label htmlFor="payMode">Payment Mode *</Label>
              <Select
                value={formData.payMode || ""}
                onValueChange={(value) =>
                  handleChange({ id: "payMode", value })
                }
              >
                <SelectTrigger className="w-full border p-1 rounded-md">
                  <SelectValue placeholder="Select Payment mode" />
                </SelectTrigger>
                <SelectContent>
                  {["CASH", "UPI", "CHECK"].map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {mode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Course Selection */}
            <div className="space-y-2">
              <Label htmlFor="course">Select a Course *</Label>
              <Select
                value={formData.course || ""}
                onValueChange={(value) => handleChange({ id: "course", value })}
              >
                <SelectTrigger className="w-full border p-1 rounded-md">
                  <SelectValue placeholder="Select Course" />
                </SelectTrigger>
                <SelectContent>
                  {courses?.map((course, idx) => (
                    <SelectItem key={idx} value={course?.course}>
                      {course.course}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mode of Study */}
            <div className="space-y-2">
              <Label htmlFor="mode">Mode of Study *</Label>
              <Select
                value={formData.mode || ""}
                onValueChange={(value) => handleChange({ id: "mode", value })}
              >
                <SelectTrigger className="w-full border p-1 rounded-md">
                  <SelectValue placeholder="Select Mode of Study" />
                </SelectTrigger>
                <SelectContent>
                  {["Offline", "Online", "Hybrid"].map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {mode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Batch and Current Technology */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="batch">Batch *</Label>
              <Select
                value={formData.batch || ""}
                onValueChange={(value) => handleChange({ id: "batch", value })}
              >
                <SelectTrigger className="w-full border p-1 rounded-md">
                  <SelectValue placeholder="Select a batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches?.map((batch: Batch) => (
                    <SelectItem key={batch._id} value={batch.bCode}>
                      {batch.bCode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* <div className="space-y-2">
              <Label htmlFor="curtech">
                Current Technology (Access lectures) *
              </Label>
              <Select
                value={formData.curtech || ""}
                onValueChange={(value) =>
                  handleChange({ id: "curtech", value })
                }
              >
                <SelectTrigger className="w-full border p-1 rounded-md">
                  <SelectValue placeholder="Select Current Technology" />
                </SelectTrigger>
                <SelectContent>
                  {[
                    "HTML",
                    "CSS",
                    "JavaScript",
                    "React",
                    "Backend",
                    "Basic",
                    "Intermediate",
                    "Advance",
                  ].map((tech) => (
                    <SelectItem key={tech} value={tech}>
                      {tech}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div> */}
          </div>

          {/* Course Completion & Placement Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                id: "isCompleted",
                label: "Course Completed",
                required: true,
                options: ["Yes", "No"],
              },
              {
                id: "isPlaced",
                label: "Placed",
                required: true,
                options: ["Yes", "No"],
              },
            ].map(({ id, label, options, required }) => (
              <div className="space-y-2" key={id}>
                <Label>
                  {label} {required && "*"}
                </Label>
                <RadioGroup
                  value={formData[id] || "No"}
                  onValueChange={(value) => handleChange({ id, value })}
                  className="flex space-x-4"
                >
                  {options.map((value) => (
                    <div className="flex items-center space-x-2" key={value}>
                      <RadioGroupItem value={value} id={`${id}-${value}`} />
                      <Label htmlFor={`${id}-${value}`}>{value}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ))}
            {/* <div className="flex flex-col">
              <Label>Rank</Label>
              <input
                type="text"
                placeholder="Enter Student Rank"
                id="rank"
                className="border px-2 p-1 rounded my-2"
                value={formData.rank}
                onChange={handleChange}
              />
            </div> */}
          </div>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              disabled={isUpdating || isLoading}
              type="submit"
              className="w-full md:w-auto min-w-[200px]"
            >
              {isUpdating || isLoading ? <Spinner /> : id ? "Update" : "Submit"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddStudents;
